const GOOGLE_TAG_ID =
  process.env.REACT_APP_GA_MEASUREMENT_ID || "G-J8YZSJE1T9";

let gaScriptPromise = null;
let gaConfigured = false;

const ensureDataLayer = () => {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
};

const removeAnalyticsCookies = () => {
  if (typeof document === "undefined") return;

  const hostParts = window.location.hostname.split(".");
  const domainCandidates = [
    window.location.hostname,
    `.${window.location.hostname}`,
  ];

  if (hostParts.length >= 2) {
    const rootDomain = hostParts.slice(-2).join(".");
    domainCandidates.push(rootDomain, `.${rootDomain}`);
  }

  ["_ga", `_ga_${GOOGLE_TAG_ID.replace(/[^a-zA-Z0-9]/g, "_")}`, "_gid", "_gat"].forEach(
    (name) => {
      document.cookie = `${name}=; Max-Age=0; path=/`;
      domainCandidates.forEach((domain) => {
        document.cookie = `${name}=; Max-Age=0; path=/; domain=${domain}`;
      });
    }
  );
};

const loadGoogleScript = () => {
  if (!GOOGLE_TAG_ID || typeof document === "undefined") {
    return Promise.resolve(false);
  }

  if (document.querySelector(`script[data-qubiq-ga="${GOOGLE_TAG_ID}"]`)) {
    return Promise.resolve(true);
  }

  if (gaScriptPromise) return gaScriptPromise;

  gaScriptPromise = new Promise((resolve) => {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
      GOOGLE_TAG_ID
    )}`;
    script.dataset.qubiqGa = GOOGLE_TAG_ID;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  });

  return gaScriptPromise;
};

const setConsentDefaults = () => {
  ensureDataLayer();
  window.gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
  });
};

export const applyGoogleConsent = ({ analytics = false, marketing = false } = {}) => {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;

  window.gtag("consent", "update", {
    analytics_storage: analytics ? "granted" : "denied",
    ad_storage: marketing ? "granted" : "denied",
    ad_user_data: marketing ? "granted" : "denied",
    ad_personalization: marketing ? "granted" : "denied",
    functionality_storage: "granted",
    security_storage: "granted",
  });
};

export const enableGoogleAnalytics = async (consent) => {
  if (!GOOGLE_TAG_ID) return false;

  ensureDataLayer();
  setConsentDefaults();

  const loaded = await loadGoogleScript();
  if (!loaded) return false;

  window[`ga-disable-${GOOGLE_TAG_ID}`] = false;
  window.gtag("js", new Date());
  applyGoogleConsent(consent);

  if (!gaConfigured) {
    window.gtag("config", GOOGLE_TAG_ID, {
      send_page_view: false,
      anonymize_ip: true,
    });
    gaConfigured = true;
  }

  return true;
};

export const disableGoogleAnalytics = () => {
  if (!GOOGLE_TAG_ID || typeof window === "undefined") return;

  ensureDataLayer();
  window[`ga-disable-${GOOGLE_TAG_ID}`] = true;
  applyGoogleConsent({ analytics: false, marketing: false });
  removeAnalyticsCookies();
};

export const trackPageView = (path) => {
  if (!GOOGLE_TAG_ID || typeof window === "undefined" || typeof window.gtag !== "function") {
    return;
  }

  if (window[`ga-disable-${GOOGLE_TAG_ID}`]) return;

  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
};

export const trackPerformanceEvent = (name, params = {}) => {
  if (!GOOGLE_TAG_ID || typeof window === "undefined" || typeof window.gtag !== "function") {
    return;
  }

  if (window[`ga-disable-${GOOGLE_TAG_ID}`]) return;

  window.gtag("event", name, params);
};

export const getGoogleTagId = () => GOOGLE_TAG_ID;
